import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, ExternalLink, MapPin, Wallet, Users, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * SplitPay — public friend-facing page for `/split/:code`.
 * ────────────────────────────────────────────────────────
 * Renders the amount + note + optional trip context and offers a PayPal
 * approval flow. Reuses the shared `POST /split-requests/{code}/paypal/*`
 * endpoints; no login required — the code itself is the auth.
 */
export default function SplitPay() {
  const { t } = useTranslation();
  const { code } = useParams();
  const [params] = useSearchParams();
  const [sr, setSr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/split-requests/${code}`);
      setSr(r.data.split_request);
    } catch (e) {
      setSr(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  // If we returned from PayPal, capture the payment.
  useEffect(() => {
    if (params.get("paypal") !== "return") return;
    const paypalOrderId = params.get("token") || params.get("paypal_order_id");
    if (!paypalOrderId) return;
    (async () => {
      try {
        await api.post(`/split-requests/${code}/paypal/capture/${paypalOrderId}`);
        toast.success(t("split.paid", "Payment complete — thanks!"));
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line
  }, [params, code]);

  const payWithPayPal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/split-requests/${code}/paypal/create`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.approve_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-electric-teal" size={22} />
      </div>
    );
  }
  if (!sr) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <XCircle size={40} className="mx-auto text-white/40 mb-3" />
        <h1 className="text-2xl font-black text-white">{t("split.notFoundTitle", "Split request not found")}</h1>
        <p className="text-sm text-white/60 mt-2">
          {t("split.notFoundHint", "The link may have expired. Ask your friend to send a fresh one.")}
        </p>
        <Link to="/" className="mt-6 inline-block bg-electric-teal text-obsidian rounded-full px-5 py-2.5 text-xs font-black">
          {t("split.backHome", "Back to Drivons")}
        </Link>
      </div>
    );
  }

  const paid = sr.status === "paid";
  const amt = Number(sr.amount || 0).toFixed(2);
  const currency = (sr.currency || "USD").toUpperCase();

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10" data-testid="split-pay-page">
      <div className="bg-ink-surface border border-white/10 rounded-3xl overflow-hidden">
        {/* Header */}
        <div className={`p-6 pb-5 text-center bg-gradient-to-b ${paid ? "from-emerald-500/25" : "from-electric-teal/25"} to-transparent`}>
          <div className={`w-14 h-14 rounded-full ${paid ? "bg-emerald-400" : "bg-electric-teal"} text-obsidian flex items-center justify-center mx-auto shadow-lg`}>
            {paid ? <CheckCircle2 size={30} strokeWidth={2.5} /> : <Users size={26} strokeWidth={2.5} />}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.28em] font-black mt-3 ${paid ? "text-emerald-300" : "text-electric-teal"}`}>
            {paid ? t("split.paidEyebrow", "Paid — thank you") : t("split.eyebrowFriend", "Your friend is asking you to chip in")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1 text-white" data-testid="split-amount">
            {amt} {currency}
          </h1>
          <div className="text-xs text-white/60 mt-1">
            {t("split.fromRider", "From {{name}}", { name: sr.rider_name || "your friend" })}
          </div>
        </div>

        {/* Note */}
        {sr.note && (
          <div className="px-5 py-4 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-1">{t("split.noteSection", "Note")}</div>
            <div className="text-sm text-white leading-relaxed" data-testid="split-note">{sr.note}</div>
          </div>
        )}

        {/* Trip context */}
        {sr.order_id && (
          <div className="px-5 py-4 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-2">{t("split.tripSection", "Original trip")}</div>
            <div className="flex items-start gap-2 text-[13px] text-white/80">
              <MapPin size={13} className="text-electric-teal shrink-0 mt-0.5" />
              <span className="truncate">{t("split.orderRef", "Order")} <span className="font-mono text-[11px] text-white/50">{sr.order_id}</span></span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-5 border-t border-white/[0.06]">
          {paid ? (
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center" data-testid="split-paid-state">
              <div className="text-sm font-black text-emerald-300">{t("split.thanks", "Thanks — payment complete!")}</div>
              <div className="text-[11px] text-white/60 mt-1">{t("split.settledOn", "Settled on")} {sr.paid_at ? new Date(sr.paid_at).toLocaleString() : "—"}</div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={payWithPayPal}
                disabled={busy}
                className="w-full rounded-2xl bg-[#ffc439] text-obsidian h-14 font-black text-[15px] inline-flex items-center justify-center gap-2 hover:brightness-95 disabled:opacity-60 transition"
                data-testid="split-pay-paypal-btn"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} strokeWidth={2.5} />}
                {t("split.payWithPaypal", "Pay {{amt}} {{cur}} with PayPal", { amt, cur: currency })}
                {!busy && <ExternalLink size={12} />}
              </button>
              <div className="mt-3 text-[11px] text-white/50 text-center leading-relaxed">
                {t("split.secure", "Secure PayPal checkout — no Drivons account required. Your friend gets a confirmation once the payment clears.")}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
